// src/utils/formatUtils.ts
export const formatName = (value: string): string => {
  // Remove números e caracteres especiais, mantendo letras e espaços
  let formatted = value.replace(/[^a-zA-Z\s]/g, '');
  
  // Divide em partes, preservando espaços
  const parts = formatted.split(/(\s+)/).filter(Boolean);
  
  // Formata cada palavra (capitaliza a primeira letra)
  formatted = parts
    .map((part) => {
      if (/\s+/.test(part)) {
        // Mantém espaços como estão
        return part;
      }
      if (part.length > 0) {
        // Capitaliza a primeira letra da palavra
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      }
      return part;
    })
    .join('');
  
  return formatted;
};

export const validateName = (value: string): boolean => {
  // Remove espaços extras para validação
  const words = value.trim().split(/\s+/).filter(Boolean);
  // Verifica se há pelo menos uma palavra e todas começam com maiúscula
  return words.length > 0 && words.every((word) => /^[A-Z]/.test(word));
};

export const formatCpf = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 11); // Remove não-dígitos e limita a 11
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

export const validateCpf = (value: string): boolean => {
  const digits = value.replace(/\D/g, ''); // Remove qualquer coisa que não seja dígito
  return /^\d{11}$/.test(digits);
};

export const formatWhatsapp = (value: string): string => {
  // Remove tudo exceto números e o sinal de +
  const digits = value.replace(/[^\d+]/g, '');

  // Caso seja um número bruto do backend (ex.: +5542988888788)
  if (digits.startsWith('+') && digits.length === 13) {
    const countryCode = '+55';
    const ddd = digits.slice(3, 5); // 42
    const number = digits.slice(5, 13); // 988888788
    if (number.length === 9) {
      return `${countryCode} (${ddd})${number.slice(0, 5)}-${number.slice(5)}`; // +55 (42)98888-8788
    }
  }

  // Formatação progressiva para entrada manual
  const cleaned = digits.startsWith('+55') ? digits.slice(3) : digits.replace(/[^\d]/g, '');
  const prefix = '+55 ';
  if (!cleaned || cleaned.length < 1) return prefix;
  if (cleaned.length <= 2) return `${prefix}(${cleaned}`;
  if (cleaned.length <= 7) return `${prefix}(${cleaned.slice(0, 2)})${cleaned.slice(2)}`;
  return `${prefix}(${cleaned.slice(0, 2)})${cleaned.slice(2, 7)}-${cleaned.slice(7, 11)}`;
};

export const validateWhatsapp = (value: string): boolean => {
  return /^\+55 \(\d{2}\)\d{5}-\d{4}$/.test(value);
};

export const formatEmail = (value: string): string => {
  return value.toLowerCase();
};

export const validateEmail = (value: string): boolean => {
  return /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/.test(value);
};

export const formatUsername = (value: string): string => {
  return value.toLowerCase().replace(/[^a-z0-9.-]/g, '');
};

export const validateUsername = (value: string): boolean => {
  return /^[a-z0-9.-]+$/.test(value);
};

// ---------------------------------------------------------------------------
// Datas
//
// O backend (api-blueltSystem) serializa datas como `java.time` SEM offset:
//   LocalDateTime -> '2026-06-17T18:57:05.695381'
//   LocalDate     -> '2026-04-30'
// Esses valores JÁ são horário local (America/Sao_Paulo) — são relógio de
// parede, não instantes. Por isso a formatação abaixo é puramente textual:
// não constrói `Date` nem usa `Intl`.
//
// A implementação anterior fazia `Date.UTC(...)` (declarando os componentes
// como UTC) e depois formatava com `timeZone: 'America/Sao_Paulo'` (UTC−3),
// subtraindo 3h de toda data do sistema e fazendo o dia retroceder antes das
// 03:00. Essa era a issue #14. Converter fuso sobre um valor que não tem fuso
// é a operação errada por definição — a correção é remover a conversão, não
// equilibrá-la.
//
// Se algum endpoint passar a enviar data COM offset ('Z' ou '-03:00'), o valor
// deixa de ser relógio de parede e estas funções não sabem convertê-lo. Nesse
// caso devolvemos a string crua (ver HAS_OFFSET abaixo): falhar visivelmente é
// melhor que exibir o horário UTC como se fosse local — esse seria o mesmo erro
// silencioso de 3h da issue #14, só que invertido e sem sintoma na tela.
// ---------------------------------------------------------------------------

// LocalDate puro: '2026-04-30'
const ISO_DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
// LocalDateTime: '2026-04-30T18:57' (segundos e milissegundos opcionais)
const ISO_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/;
// Sufixo de fuso: 'Z', '+00:00', '-0300'. Não casa com LocalDate ('2026-04-30'),
// porque exige 4 dígitos após o sinal.
const HAS_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/;
// Forma exigida pelo guard de `ultimoAcesso` — data + hora completa com segundos
const ISO_DATE_TIME_WITH_SECONDS = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

// Valida se a string é uma data ISO 8601 com hora completa.
// Movida de ProtectedLayout.tsx (dedup) — comportamento preservado na íntegra,
// incluindo a checagem via `Date`. Aqui o `Date` serve apenas para validar;
// nenhum componente é extraído dele para formatação, então não reintroduz o
// bug de fuso.
export const isValidDateString = (dateString: string): boolean => {
  if (!dateString || typeof dateString !== 'string') return false;
  return ISO_DATE_TIME_WITH_SECONDS.test(dateString) && !isNaN(new Date(dateString).getTime());
};

// Formata data sem tempo (DD/MM/AAAA).
// Aceita LocalDate e LocalDateTime; devolve a string original se não reconhecer.
export const formatDate = (dateString: string): string => {
  if (!dateString) return ''; // Retorna vazio se a data for inválida ou inexistente
  if (HAS_OFFSET.test(dateString)) return dateString; // Valor com fuso: não sabemos converter
  const match = ISO_DATE_TIME.exec(dateString) ?? ISO_DATE_ONLY.exec(dateString);
  if (!match) return dateString; // Retorna a string original se a data for inválida
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
};

// Formata data com tempo (DD/MM/AAAA - HH:MMh).
// Recebendo um LocalDate puro (sem hora), degrada para DD/MM/AAAA em vez de
// quebrar — era esse crash que os wrappers locais contornavam.
export const formatDateTime = (dateString: string): string => {
  if (!dateString) return '';
  if (HAS_OFFSET.test(dateString)) return dateString; // Valor com fuso: não sabemos converter
  const match = ISO_DATE_TIME.exec(dateString);
  if (!match) return formatDate(dateString);
  const [, year, month, day, hours, minutes] = match;
  return `${day}/${month}/${year} - ${hours}:${minutes}h`;
};

// Variante que exibe '—' para valor ausente, em vez de string vazia.
// Consolida os 3 helpers `formatLocalOrDate`/`formatLocalOrDateTime` que
// estavam duplicados nos SidePanels de Compra, Aprovação e Requisição.
export const formatDateOrDash = (dateString: string | null | undefined): string => {
  if (!dateString) return '—';
  return formatDate(dateString);
};

// Extrai a parte de data (yyyy-mm-dd) de um LocalDate ou LocalDateTime, textualmente.
// Retorna null se não reconhecer o formato.
const toDateKey = (dateString: string): string | null => {
  const match = ISO_DATE_TIME.exec(dateString) ?? ISO_DATE_ONLY.exec(dateString);
  if (!match) return null;
  const [, year, month, day] = match;
  return `${year}-${month}-${day}`;
};

// Chave yyyy-mm-dd de HOJE no horário local do navegador.
// `new Date()` aqui é o instante real atual — usar getters locais é correto e
// NÃO reintroduz o bug de fuso (só o parsing de valores do backend é proibido de
// passar por `Date`; ver ADR-019 e o cabeçalho deste bloco).
const todayKey = (): string => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Verdadeiro se `dateString` (previsão de entrega, LocalDate ou LocalDateTime)
// é estritamente anterior a hoje. Comparação puramente textual — chaves ISO
// yyyy-mm-dd comparam lexicograficamente na mesma ordem que cronologicamente.
//
// Substitui as cópias de `isDeliveryDateOverdue` em Conferencia/Compras, que
// faziam `new Date(dataEntrega)`: um LocalDate puro ('2026-04-30') era parseado
// como UTC e, em UTC−3, retrocedia um dia, marcando como atrasado um item que
// vence hoje (issue #18).
export const isDateOverdue = (dateString: string | null | undefined): boolean => {
  if (!dateString) return false;
  const key = toDateKey(dateString);
  if (!key) return false; // formato irreconhecível: não afirmar atraso
  return key < todayKey();
};


// Adiciona função para mascarar CPF no formato 999.***.***-99
export const maskCpf = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length < 11) return digits; // Retorna sem formatação se incompleto
  return `${digits.slice(0, 3)}.***.***-${digits.slice(9)}`;
};



export const getEmptyDetailsMessage = (tipo: string): string => { // Novo: função reutilizável para mensagem vazia baseada em tipoRequisicao, promovendo escalabilidade e limpeza
  switch (tipo) {
    case 'COMPRA':
    case 'TRANSFERENCIA':
      return 'Item ainda não teve atualizações.';
    case 'PERDIDA':
      return 'Item não terá atualizações pois é uma venda perdida.';
    default:
      return 'Nenhum dado encontrado';
  }
};